import React, { useEffect, useMemo, useState } from "react";
import { canUseMarketing } from "@/components/cookies/consentManager";

/**
 * Meta Pixel loader (client-side)
 * Loads ONLY when marketing consent is granted.
 * Pixel ID is hardcoded as requested.
 */
const PIXEL_ID = "1601773347915391";

function loadMetaPixelOnce() {
  if (typeof window === "undefined") return;
  // Already loaded
  if (window.fbq) return;

  // Meta Pixel bootstrap (same logic as the standard snippet)
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = (f.fbq = function () {
      // eslint-disable-next-line prefer-rest-params
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");

  window.fbq("init", PIXEL_ID);
}

function trackPageView() {
  if (typeof window === "undefined") return;
  // Respect current consent even after script is loaded
  if (!canUseMarketing()) return;
  if (!window.fbq) return;
  window.fbq("track", "PageView");
}

export default function MetaPixelGate() {
  const [marketingAllowed, setMarketingAllowed] = useState(() => canUseMarketing());

  // Keep in sync when consent changes (banner / preferences)
  useEffect(() => {
    const update = () => setMarketingAllowed(canUseMarketing());

    window.addEventListener("tresopilot:consent_updated", update);
    const onStorage = (e) => {
      if (e.key === "tresopilot_cookie_consent") update();
    };

    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("tresopilot:consent_updated", update);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // Load pixel when consent granted + track page views on SPA navigations
  useEffect(() => {
    if (!marketingAllowed) return;

    loadMetaPixelOnce();
    trackPageView();

    // SPA route change tracking (without relying on react-router context)
    if (typeof window === "undefined") return;

    if (window.__tresopilot_meta_pixel_history_patched) return;
    window.__tresopilot_meta_pixel_history_patched = true;

    const _pushState = history.pushState;
    const _replaceState = history.replaceState;

    const onNav = () => {
      // Track PageView on any navigation change
      trackPageView();
    };

    history.pushState = function () {
      // eslint-disable-next-line prefer-rest-params
      _pushState.apply(this, arguments);
      onNav();
    };

    history.replaceState = function () {
      // eslint-disable-next-line prefer-rest-params
      _replaceState.apply(this, arguments);
      onNav();
    };

    window.addEventListener("popstate", onNav);

    return () => {
      // We keep the patch in place to avoid edge cases; only remove popstate.
      window.removeEventListener("popstate", onNav);
    };
  }, [marketingAllowed]);

  // noscript beacon (kept for parity with the standard snippet; only rendered when consent granted)
  const noscriptHtml = useMemo(() => {
    const src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`;
    return `<img height="1" width="1" style="display:none" src="${src}" />`;
  }, []);

  if (!marketingAllowed) return null;
  return <noscript dangerouslySetInnerHTML={{ __html: noscriptHtml }} />;
}
